import * as React from "react";
import { Container, Divider, Grid, Header } from "semantic-ui-react";

import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";

import { AccordionQuestionnaireContainer } from "../../questionnaires/components/AccordionQuestionnaireContainer";
import { AccordionQuestionnaireSection } from "../../questionnaires/components/AccordionQuestionnaireSection";
import { QuestionConfig } from "../../questionnaires/questionnaireConfig";
import { RadioButtonQuestion, RadioButton, YesOrNo } from "../../questionnaires/components/questions/RadioButtonQuestion";
import { DropdownQuestion } from "../../questionnaires/components/questions/DropdownQuestion";
import { CheckboxQuestion } from "../../questionnaires/components/questions/CheckboxQuestion";
import { TaskModel } from "../../task/taskActions/models";
import {
    DATE_FORMAT,
    ManagerFeedbackOnReportQuestionnaireModel,
    ManagerFeedbackOnReportSections,
    ManagerFeedbackOnReportSectionTitles,
    MANAGER_FEEDBACK_ON_REPORT_KEY,
    overallExperienceOptions,
    ParentConsultationModel,
    RecommendationsRealisticityLevel,
    returnToWorkDateAccuracyOptions,
    wereRecomendationsRealisticOptions
} from "../models";
import * as questionIds from "../managerFeedbackOnReportQuestionsIds";
import { managerFeedbackOnReportQuestionsMasterConfig as masterConfig } from "../managerFeedbackOnReportMasterConfig";
import { QuestionAnswer, QuestionnaireTypes } from "../../questionnaires/model";
import { TextQuestion } from "../../questionnaires/components/questions/TextQuestion";
import { getAdminProfessionDisplayName } from "../../adminUser/models";
import { OhCase } from "../../model";

import "../styles/feedback.scss";

interface OwnProps {
    customerId: string;
    ohCase: OhCase;
    task: TaskModel;
    parentConsultation: ParentConsultationModel;
    loadQuestionnaire?: (customerId: string, ohCaseId: string, taskId: string) => Promise<void>;
    submitQuestionnaire?: (questionnaire: ManagerFeedbackOnReportQuestionnaireModel) => Promise<boolean>;
    questionnaire: ManagerFeedbackOnReportQuestionnaireModel;
}

export const ManagerFeedbackOnReportQuestionnaire: React.FunctionComponent<OwnProps> = (props: OwnProps) => {

    const isReadonly = props.task.completed === true;

    const loadQuestionnaire = () => {
        if (props.loadQuestionnaire) {
            props.loadQuestionnaire(props.customerId, props.ohCase.id, props.task.id);
        }
    };

    const getQuestionConfig = (questionId: string): QuestionConfig => {
        return masterConfig.find(c => c.id === questionId) || { id: questionId, disabled: true };
    };

    const getAnswerToQuestionWithId = (id: string): QuestionAnswer => {
        return props.questionnaire?.questions?.find(question => question.id === id)?.answer;
    };

    const wereRecommendationsRealisticAnswer = getAnswerToQuestionWithId(questionIds.WereRecommendationsRealistic) as string;
    const wereRecommendationsUnrealistic =
        wereRecommendationsRealisticAnswer === RecommendationsRealisticityLevel.SomewhatUnrealistic ||
        wereRecommendationsRealisticAnswer === RecommendationsRealisticityLevel.VeryUnrealistic;

    return (
        <Container className="referral-report">
            <Grid container>
                <Grid.Row className="report-title-row">
                    <Grid.Column>
                        <Header as="h1">Manager Feedback on Report</Header>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            <Grid container className="details-grid">
                <Grid.Row className="report-section-row">
                    <Grid.Column>
                        <Header as="h4">Details</Header>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Employee
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        {props.ohCase.employeeFirstName} {props.ohCase.employeeLastName}
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Consultation Type
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        {props.parentConsultation.taskDescription}
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Consultation Completed By
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        {/* eslint-disable-next-line max-len */}
                        {props.parentConsultation.consultationCompletedBy?.name} {getAdminProfessionDisplayName(props.parentConsultation?.consultationUserProfession)}
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Date of Consultation
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        {props.parentConsultation?.consultationCompletedDate?.format(DATE_FORMAT)}
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Report Completed By
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        {props.parentConsultation.reportCompletedBy?.name} {getAdminProfessionDisplayName(props.parentConsultation.reportUserProfession)}
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Date of Report
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        {props.parentConsultation?.reportCompletedDate?.format(DATE_FORMAT)}
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            <Grid container>
                <Grid.Row>
                    <Grid.Column>
                        <p>
                            The information collected in this survey shall be used to evaluate our performance and customer
                            satisfaction and identify any areas that require improvement. Anonymous reports showing average
                            scores will be shared with our customers but individual names will not be mentioned.
                        </p>
                        <p>
                            You can find out more information about how Optima Health process their data by visiting our
                            <a href="https://tphealth.co.uk/privacy-statement" target="_blank" rel="noopener noreferrer"> Privacy Policy</a> online.
                        </p>
                        <p className="manager-warning-note">
                            Please do not use this form to ask further questions about the report.
                            Please contact us directly if you have further questions or use our case conversation feature which
                            can be found on any of the cases you have access to.
                        </p>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            <Container>
                <Grid>
                    <AccordionQuestionnaireContainer
                        loadQuestionnaire={loadQuestionnaire}
                        submitQuestionnaire={() => props.submitQuestionnaire(props.questionnaire)}
                        readonly={isReadonly}
                        questionnaireKey={MANAGER_FEEDBACK_ON_REPORT_KEY}
                        questionnaireType={QuestionnaireTypes.Other}
                    >
                        <AccordionQuestionnaireSection
                            title={ManagerFeedbackOnReportSectionTitles.FeedbackSurvey}
                            id={ManagerFeedbackOnReportSections.FeedbackSurvey}
                            hideNextButton={true}
                            active={true}
                        >
                            <RadioButtonQuestion
                                questionConfig={getQuestionConfig(questionIds.LanguageClearAndConcise)}
                                readonly={isReadonly}
                                mandatory={true}
                            >
                                <RadioButton label="Yes" value={YesOrNo.Yes} />
                                <RadioButton label="No" value={YesOrNo.No} />
                            </RadioButtonQuestion>
                            <RadioButtonQuestion
                                questionConfig={getQuestionConfig(questionIds.ReportAnswersAllTheQuestions)}
                                readonly={isReadonly}
                                mandatory={true}
                            >
                                <RadioButton label="Yes" value={YesOrNo.Yes} />
                                <RadioButton label="No" value={YesOrNo.No} />
                            </RadioButtonQuestion>
                            <RadioButtonQuestion
                                questionConfig={getQuestionConfig(questionIds.ReportSpecificEnough)}
                                readonly={isReadonly}
                                mandatory={true}
                            >
                                <RadioButton label="Yes" value={YesOrNo.Yes} />
                                <RadioButton label="No" value={YesOrNo.No} />
                            </RadioButtonQuestion>
                            <DropdownQuestion
                                questionConfig={getQuestionConfig(questionIds.WereRecommendationsRealistic)}
                                readonly={isReadonly}
                                mandatory={true}
                                options={wereRecomendationsRealisticOptions.sort(o => o.order)}
                                hideDivider
                            />
                            {wereRecommendationsUnrealistic &&
                                <TextQuestion
                                    questionConfig={getQuestionConfig(questionIds.WereRecommendationsRealisticFurtherComments)}
                                    readonly={isReadonly}
                                    mandatory={true}
                                    multiline
                                    hideDivider
                                />
                            }
                            <Divider />
                            <DropdownQuestion
                                questionConfig={getQuestionConfig(questionIds.HowAccurateWasReturnToWorkDate)}
                                readonly={isReadonly}
                                mandatory={true}
                                options={returnToWorkDateAccuracyOptions.sort(o => o.order)}
                                filterOldOptions={true}
                            />

                            <MultiButtonQuestion
                                questionConfig={getQuestionConfig(questionIds.OverallExperience)}
                                readonly={isReadonly}
                                mandatory={true}
                                values={overallExperienceOptions}
                            />
                            <TextQuestion
                                questionConfig={getQuestionConfig(questionIds.FurtherComments)}
                                readonly={isReadonly}
                                mandatory={true}
                                multiline
                            />
                            <CheckboxQuestion
                                questionConfig={getQuestionConfig(questionIds.FeedbackContactConsent)}
                                readonly={isReadonly}
                                value={YesOrNo.Yes}
                            />
                        </AccordionQuestionnaireSection>
                    </AccordionQuestionnaireContainer>
                </Grid>
            </Container>
        </Container>
    );
};
